import { useState } from 'react';

export interface Period {
  start: string; // YYYY-MM-DD
  end: string; // YYYY-MM-DD
}

/**取相对当月偏移 N 个月的自然月区间（0=当月，-1=上月） */
export function monthRange(offset: number): Period {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth() + offset, 1);
  const start = new Date(d.getFullYear(), d.getMonth(), 1);
  const end = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  const fmt = (x: Date) =>
    `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
  return { start: fmt(start), end: fmt(end) };
}

interface Props {
  period: Period;
  onChange: (p: Period) => void;
}

/**账期选择条：上月 | 区间(点击弹出日期面板) | 下月 | 回当月 */
export default function PeriodPicker({ period, onChange }: Props) {
  const [panelOpen, setPanelOpen] = useState(false);
  const [draft, setDraft] = useState<Period>(period);

  const current = monthRange(0);
  const prevMonth = () => {
    const offset = monthOffset(period, -1);
    onChange(monthRange(offset));
  };
  const nextMonth = () => {
    const offset = monthOffset(period, 1);
    onChange(monthRange(offset));
  };

  //当前 period 起点落在几个月前（以当月为 0 基准）
  function monthOffset(p: Period, delta: number): number {
    const [y, m] = p.start.split('-').map(Number);
    const [cy, cm] = current.start.split('-').map(Number);
    return (y - cy) * 12 + (m - cm) + delta;
  }

  const isCurrentMonth = period.start === current.start && period.end === current.end;
  const isFutureMonth = period.start > current.start;

  const openPanel = () => {
    setDraft(period);
    setPanelOpen(true);
  };

  const confirm = () => {
    const p = draft.start > draft.end ? { start: draft.end, end: draft.start } : draft;
    onChange(p);
    setPanelOpen(false);
  };

  return (
    <>
      <div className="bg-white rounded-2xl px-4 py-3 flex items-center justify-between border border-background-100">
        <button
          onClick={prevMonth}
          className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-background-100 transition-colors cursor-pointer"
          aria-label="上一月"
        >
          <i className="ri-arrow-left-s-line text-foreground-600 text-lg"></i>
        </button>

        <button
          onClick={openPanel}
          className="flex items-center gap-2 cursor-pointer hover:opacity-70 transition-opacity"
          aria-label="选择时间区间"
        >
          <i className="ri-calendar-line text-foreground-400 text-sm"></i>
          <span className="text-foreground-700 text-sm font-medium">
            {period.start} - {period.end}
          </span>
        </button>

        <div className="flex items-center gap-1">
          <button
            onClick={nextMonth}
            disabled={isFutureMonth}
            className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-background-100 disabled:opacity-30 transition-colors cursor-pointer"
            aria-label="下一月"
          >
            <i className="ri-arrow-right-s-line text-foreground-600 text-lg"></i>
          </button>
          <button
            onClick={() => onChange(monthRange(0))}
            disabled={isCurrentMonth}
            className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-background-100 disabled:opacity-30 transition-colors cursor-pointer"
            aria-label="回到本月"
          >
            <i className="ri-refresh-line text-foreground-400 text-sm"></i>
          </button>
        </div>
      </div>

      {/* 时间区间选择面板 */}
      {panelOpen && (
        <div className="fixed inset-0 z-50 flex items-end justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setPanelOpen(false)}></div>
          <div className="relative w-full max-w-md bg-white rounded-t-2xl px-5 pt-5 pb-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-foreground-800 text-sm font-semibold">选择时间区间</span>
              <button
                onClick={() => setPanelOpen(false)}
                className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-background-100 cursor-pointer"
                aria-label="关闭"
              >
                <i className="ri-close-line text-foreground-500 text-lg"></i>
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-foreground-500 text-xs mb-1.5">开始日期</label>
                <input
                  type="date"
                  value={draft.start}
                  onChange={(e) => setDraft({ ...draft, start: e.target.value })}
                  className="w-full h-10 px-3 rounded-xl border border-background-200 text-sm text-foreground-800 focus:outline-none focus:border-primary-400"
                />
              </div>
              <div>
                <label className="block text-foreground-500 text-xs mb-1.5">结束日期</label>
                <input
                  type="date"
                  value={draft.end}
                  onChange={(e) => setDraft({ ...draft, end: e.target.value })}
                  className="w-full h-10 px-3 rounded-xl border border-background-200 text-sm text-foreground-800 focus:outline-none focus:border-primary-400"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button
                onClick={() => setPanelOpen(false)}
                className="flex-1 h-10 rounded-full border border-background-200 text-foreground-600 text-sm font-medium hover:bg-background-100 transition-colors cursor-pointer"
              >
                取消
              </button>
              <button
                onClick={confirm}
                className="flex-1 h-10 rounded-full bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer"
              >
                确定
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
